import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { sendUserNotification } from "./notificationApi";
import { openBrandedPrescription } from "./prescriptionUtils";
import {
  canJoinConsultationCall,
  getConsultationJoinLabel,
  isVetConsultationBooking,
  openConsultationCallWindow,
} from "./consultationUtils";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

export default function OwnerBookings({ user, onHome }) {
  const REASSIGN_BOOKING_URL =
    process.env.REACT_APP_REASSIGN_BOOKING_URL ||
    "https://us-central1-petaar-3dcab.cloudfunctions.net/reassignBooking";
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reviewsByBooking, setReviewsByBooking] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState("");
  const [updatingBookingId, setUpdatingBookingId] = useState("");
  const [cancelReasonByBooking, setCancelReasonByBooking] = useState({});
  const [rescheduleDateByBooking, setRescheduleDateByBooking] = useState({});
  const [rescheduleReasonByBooking, setRescheduleReasonByBooking] = useState({});
  const [retryingAssignmentId, setRetryingAssignmentId] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [refundFilter, setRefundFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [prescriptionsByBooking, setPrescriptionsByBooking] = useState({});
  const handleLogoHomeClick = useCallback(() => {
    if (typeof onHome === "function") {
      onHome();
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }, [onHome]);

  const fetchBookings = useCallback(async () => {
      const ownerEmail = String(user?.email || "").trim();
      const ownerUid = String(user?.uid || "").trim();
      if (!ownerUid && !ownerEmail) {
        setBookings([]);
        return;
      }

      setLoading(true);
      try {
        setLoadError("");
        const bookingQueries = [];
        if (ownerEmail) {
          bookingQueries.push(query(
            collection(db, "bookings"),
            where("ownerEmail", "==", ownerEmail),
          ));
          bookingQueries.push(query(
            collection(db, "bookings"),
            where("userEmail", "==", ownerEmail),
          ));
        }
        if (ownerUid) {
          bookingQueries.push(query(
            collection(db, "bookings"),
            where("ownerUid", "==", ownerUid),
          ));
        }
        const bookingSnaps = await Promise.all(bookingQueries.map((bookingQuery) => getDocs(bookingQuery)));
        const merged = bookingSnaps.flatMap((snapshot) => snapshot.docs);
        const byId = new Map(merged.map((d) => [d.id, { id: d.id, ...d.data() }]));
        setBookings([...byId.values()]);

        if (ownerEmail) {
          const reviewQ = query(
            collection(db, "reviews"),
            where("ownerEmail", "==", ownerEmail),
          );
          const reviewSnap = await getDocs(reviewQ);
          const reviewMap = {};
          reviewSnap.docs.forEach((d) => {
            const review = d.data();
            if (review.bookingId) {
              reviewMap[review.bookingId] = { id: d.id, ...review };
            }
          });
          setReviewsByBooking(reviewMap);
        } else {
          setReviewsByBooking({});
        }
      } catch (error) {
        setLoadError(error.message || "Unable to load bookings.");
        setBookings([]);
      } finally {
        setLoading(false);
      }
    }, [user?.email, user?.uid]);

  useEffect(() => {
    const ownerEmail = String(user?.email || "").trim();
    const ownerUid = String(user?.uid || "").trim();
    if (!ownerUid && !ownerEmail) {
      setBookings([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setLoadError("");

    const rowMaps = new Map();
    const readiness = new Map();
    const queryConfigs = [];
    if (ownerEmail) {
      queryConfigs.push({
        key: "ownerEmail",
        value: query(collection(db, "bookings"), where("ownerEmail", "==", ownerEmail)),
      });
      queryConfigs.push({
        key: "legacyEmail",
        value: query(collection(db, "bookings"), where("userEmail", "==", ownerEmail)),
      });
    }
    if (ownerUid) {
      queryConfigs.push({
        key: "ownerUid",
        value: query(collection(db, "bookings"), where("ownerUid", "==", ownerUid)),
      });
    }
    queryConfigs.forEach((item) => {
      rowMaps.set(item.key, new Map());
      readiness.set(item.key, false);
    });

    const applyRows = () => {
      const merged = new Map();
      rowMaps.forEach((rows) => {
        rows.forEach((value, key) => {
          merged.set(key, value);
        });
      });
      setBookings([...merged.values()]);
    };
    const finishLoading = () => {
      const allReady = [...readiness.values()].every((flag) => flag === true);
      if (allReady) {
        setLoading(false);
      }
    };

    const unsubscribeList = queryConfigs.map((item) => onSnapshot(
      item.value,
      (snapshot) => {
        readiness.set(item.key, true);
        const currentRows = rowMaps.get(item.key);
        currentRows?.clear();
        snapshot.docs.forEach((docSnap) => {
          currentRows?.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
        setLoadError("");
        applyRows();
        finishLoading();
      },
      (error) => {
        readiness.set(item.key, true);
        setLoadError(error.message || "Unable to load bookings.");
        finishLoading();
      },
    ));

    return () => {
      unsubscribeList.forEach((unsubscribe) => unsubscribe());
    };
  }, [user?.email, user?.uid]);

  useEffect(() => {
    if (!user?.email) {
      setReviewsByBooking({});
      return undefined;
    }
    const reviewQuery = query(collection(db, "reviews"), where("ownerEmail", "==", user.email));
    const unsubscribeReviews = onSnapshot(
      reviewQuery,
      (snapshot) => {
        const reviewMap = {};
        snapshot.docs.forEach((item) => {
          const review = item.data() || {};
          if (review.bookingId) {
            reviewMap[review.bookingId] = { id: item.id, ...review };
          }
        });
        setReviewsByBooking(reviewMap);
      },
      () => {
        setReviewsByBooking({});
      },
    );
    return () => unsubscribeReviews();
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) {
      setPrescriptionsByBooking({});
      return undefined;
    }
    const prescriptionQuery = query(collection(db, "prescriptions"), where("ownerEmail", "==", user.email));
    const unsubscribe = onSnapshot(
      prescriptionQuery,
      (snapshot) => {
        const map = {};
        snapshot.docs.forEach((item) => {
          const data = item.data() || {};
          const bookingId = String(data.bookingId || "").trim();
          if (!bookingId) return;
          const existing = map[bookingId];
          const existingTs = existing?.createdAt?.seconds || 0;
          const incomingTs = data.createdAt?.seconds || 0;
          if (!existing || incomingTs >= existingTs) {
            map[bookingId] = { id: item.id, ...data };
          }
        });
        setPrescriptionsByBooking(map);
      },
      () => {
        setPrescriptionsByBooking({});
      },
    );
    return () => unsubscribe();
  }, [user?.email]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const bookingSummary = useMemo(() => ({
    total: bookings.length,
    active: bookings.filter((b) => ["pending", "pending_assignment", "accepted", "in_progress"].includes(String(b.status || ""))).length,
    completed: bookings.filter((b) => String(b.status || "") === "completed").length,
    refunded: bookings.filter((b) => String(b.refundStatus || "") === "refund_processed").length,
  }), [bookings]);

  const sortedBookings = useMemo(
    () => [...bookings]
      .filter((b) => (statusFilter ? b.status === statusFilter : true))
      .filter((b) => (paymentFilter ? b.paymentStatus === paymentFilter : true))
      .filter((b) => (refundFilter ? (b.refundStatus || "not_requested") === refundFilter : true))
      .filter((b) => {
        const q = String(searchTerm || "").trim().toLowerCase();
        if (!q) return true;
        const haystack = [
          b.serviceTitle || b.serviceName || "",
          b.providerEmail || "",
          b.ownerEmail || "",
          b.bookingDate || "",
          b.status || "",
          b.paymentStatus || "",
          b.refundStatus || "",
          b.id || "",
        ].join(" ").toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)),
    [bookings, paymentFilter, refundFilter, searchTerm, statusFilter],
  );
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedBookings.length / Math.max(1, pageSize))),
    [sortedBookings.length, pageSize],
  );
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, paymentFilter, refundFilter, searchTerm, pageSize]);
  const pagedBookings = useMemo(() => {
    const safeSize = Math.max(1, pageSize);
    const start = (currentPage - 1) * safeSize;
    return sortedBookings.slice(start, start + safeSize);
  }, [sortedBookings, currentPage, pageSize]);

  const exportCsv = () => {
    if (!sortedBookings.length) {
      alert("No bookings to export.");
      return;
    }
    const escapeCsv = (value) => {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, "\"\"")}"`;
      }
      return text;
    };
    const headers = [
      "booking_id",
      "service_title",
      "provider_email",
      "owner_email",
      "booking_date",
      "status",
      "payment_status",
      "refund_status",
      "amount_inr",
      "platform_fee_inr",
      "provider_payout_inr",
      "created_at",
    ];
    const rows = sortedBookings.map((b) => [
      b.id || "",
      b.serviceTitle || b.serviceName || "",
      b.providerEmail || "",
      b.ownerEmail || "",
      b.bookingDate || "",
      b.status || "",
      b.paymentStatus || "",
      b.refundStatus || "not_requested",
      Number(b.amount) || 0,
      Number(b.commissionAmount) || (Number(b.amount) || 0) * 0.2,
      Number(b.providerEarning) || (Number(b.amount) || 0) * 0.8,
      b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000).toISOString() : "",
    ].map(escapeCsv).join(","));

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `owner-bookings-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getBookingStatusLabel = (booking) => {
    const status = String(booking.status || "").toLowerCase();
    if (status === "pending_assignment") return "Finding provider";
    if (status === "pending") return "Pending provider acceptance";
    if (status === "accepted") return "Accepted by provider";
    if (status === "completed") return "Completed";
    if (status === "cancelled") {
      if (booking.refundStatus === "refund_processed") return "Cancelled (Refund processed)";
      if (booking.refundStatus === "refund_requested") return "Cancelled (Refund in progress)";
      return "Cancelled";
    }
    if (status === "cancel_requested") return "Cancellation requested";
    if (status === "rejected") return "Reassigning provider";
    return booking.status || "unknown";
  };

  const getConsultationStatusLabel = (booking) => {
    const raw = String(booking?.consultationStatus || "").toLowerCase();
    if (!raw) return "pending";
    const map = {
      assignment_in_progress: "Finding vet",
      waiting_vet: "Waiting for available vet",
      vet_assigned: "Vet assigned",
      vet_accepted: "Vet accepted",
      consultation_live: "Consultation live",
      prescription_sent: "Prescription sent",
      consultation_completed: "Consultation completed",
      consultation_cancelled: "Consultation cancelled",
    };
    return map[raw] || raw.replaceAll("_", " ");
  };

  const getSlaText = (booking) => {
    const status = String(booking.status || "").toLowerCase();
    if (!["pending", "pending_assignment"].includes(status)) {
      return "";
    }
    const assignedMs =
      booking.assignedAt?.seconds ? booking.assignedAt.seconds * 1000 :
        booking.createdAt?.seconds ? booking.createdAt.seconds * 1000 : 0;
    if (!assignedMs) return "Provider response ETA not available";

    const deadlineMs = assignedMs + 20 * 60 * 1000;
    const remainingMs = deadlineMs - nowTick;
    if (remainingMs <= 0) return "Provider response overdue. Reassignment in progress.";
    const mins = Math.ceil(remainingMs / 60000);
    return `Expected provider response in ~${mins} min`;
  };

  const requestCancellation = async (booking) => {
    try {
      const reason = String(cancelReasonByBooking[booking.id] || "").trim();
      if (!reason) {
        alert("Please select cancellation reason.");
        return;
      }
      setUpdatingBookingId(booking.id);
      await updateDoc(doc(db, "bookings", booking.id), {
        status: "cancel_requested",
        cancellationReason: reason,
        cancelRequestedAt: serverTimestamp(),
      });

      if (booking.providerEmail) {
        await sendUserNotification({
          bookingId: booking.id,
          recipientEmail: booking.providerEmail,
          type: "booking_cancel_requested",
          title: "Cancellation Requested",
          message: `Owner requested cancellation for ${booking.serviceTitle || "service"}. Reason: ${reason}`,
        });
      }

      alert("Cancellation requested.");
    } catch (err) {
      alert(err.message);
    } finally {
      setUpdatingBookingId("");
    }
  };
  const isReschedulable = (booking) =>
    ["pending_assignment", "pending", "accepted"].includes(String(booking.status || "").toLowerCase());
  const requestReschedule = async (booking) => {
    try {
      const nextDate = String(rescheduleDateByBooking[booking.id] || "").trim();
      const reason = String(rescheduleReasonByBooking[booking.id] || "").trim();
      if (!nextDate) {
        alert("Please select a new booking date.");
        return;
      }
      if (!reason) {
        alert("Please select reschedule reason.");
        return;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(`${nextDate}T00:00:00`);
      if (Number.isNaN(selected.getTime()) || selected < today) {
        alert("Please select a valid future date.");
        return;
      }
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 120);
      maxDate.setHours(0, 0, 0, 0);
      if (selected > maxDate) {
        alert("Please choose a date within 120 days.");
        return;
      }
      if (String(booking.bookingDate || "") === nextDate) {
        alert("Please choose a different date than current booking date.");
        return;
      }
      if (booking.providerId) {
        const providerSnap = await getDoc(doc(db, "users", booking.providerId));
        if (providerSnap.exists()) {
          const data = providerSnap.data() || {};
          const unavailableDates = Array.isArray(data.unavailableDates) ? data.unavailableDates : [];
          if (unavailableDates.includes(nextDate)) {
            alert("Selected date is unavailable for this provider. Please choose another date.");
            return;
          }
        }
      }

      setUpdatingBookingId(booking.id);
      await updateDoc(doc(db, "bookings", booking.id), {
        bookingDate: nextDate,
        rescheduleReason: reason,
        rescheduleRequestedAt: serverTimestamp(),
      });

      if (booking.providerEmail) {
        await sendUserNotification({
          bookingId: booking.id,
          recipientEmail: booking.providerEmail,
          type: "booking_rescheduled",
          title: "Booking Rescheduled",
          message: `Booking for ${booking.serviceTitle || "service"} moved to ${nextDate}. Reason: ${reason}`,
        });
      }

      setRescheduleDateByBooking((prev) => ({ ...prev, [booking.id]: "" }));
      setRescheduleReasonByBooking((prev) => ({ ...prev, [booking.id]: "" }));
      alert("Booking date rescheduled.");
    } catch (err) {
      alert(err.message);
    } finally {
      setUpdatingBookingId("");
    }
  };

  const canRetryAssignment = (booking) => {
    const status = String(booking?.status || "").toLowerCase();
    if (!["pending_assignment", "pending"].includes(status)) return false;
    return String(booking?.paymentStatus || "").toLowerCase() === "paid";
  };

  const retryProviderAssignment = async (booking) => {
    if (!booking?.id) return;
    try {
      setRetryingAssignmentId(booking.id);
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Session expired. Please login again.");
      }
      const res = await fetch(REASSIGN_BOOKING_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ bookingId: booking.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `Assignment retry failed (${res.status})`);
      }
      if (data?.reassigned) {
        alert("Provider assigned successfully.");
      } else {
        alert("Retry requested. We are still finding an available provider.");
      }
    } catch (error) {
      alert(error.message || "Unable to retry assignment right now.");
    } finally {
      setRetryingAssignmentId("");
    }
  };

  const saveReview = async (booking) => {
    const draft = drafts[booking.id] || {};
    const rating = Number(draft.rating);
    const comment = String(draft.comment || "").trim();

    if (!Number.isFinite(rating) || rating < 1 || rating > 5) {
      alert("Please give rating between 1 and 5.");
      return;
    }
    if (!comment) {
      alert("Please write a short review comment.");
      return;
    }

    try {
      setSavingId(booking.id);
      await setDoc(doc(db, "reviews", booking.id), {
        bookingId: booking.id,
        serviceId: booking.serviceId || booking.assignedServiceId || "",
        serviceTitle: booking.serviceTitle || booking.serviceName || "",
        providerEmail: booking.providerEmail || "",
        ownerEmail: user.email,
        rating,
        comment,
        bookingDate: booking.bookingDate || "",
        statusSnapshot: booking.status || "",
        verifiedBooking: true,
        updatedAt: serverTimestamp(),
        createdAt: reviewsByBooking[booking.id]?.createdAt || serverTimestamp(),
      }, { merge: true });

      setReviewsByBooking((prev) => ({
        ...prev,
        [booking.id]: {
          ...(prev[booking.id] || {}),
          bookingId: booking.id,
          rating,
          comment,
        },
      }));
      setDrafts((prev) => ({
        ...prev,
        [booking.id]: { rating: "", comment: "" },
      }));
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingId("");
    }
  };

  const formatTs = (ts) => {
    if (!ts?.seconds) return "N/A";
    return new Date(ts.seconds * 1000).toLocaleString();
  };
  const getStatusTone = (booking) => {
    const status = String(booking?.status || "").toLowerCase();
    if (status === "completed") return "success";
    if (status === "cancelled" || status === "rejected") return "danger";
    if (status === "pending" || status === "pending_assignment" || status === "cancel_requested") return "warning";
    return "default";
  };

  // CONSULTATION CHAT: Hash navigation entry point from owner bookings list.
  const openConsultationChat = (bookingId) => {
    const safeBookingId = String(bookingId || "").trim();
    if (!safeBookingId) return;
    window.location.hash = `#/consultation/${encodeURIComponent(safeBookingId)}`;
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Bookings"
        title="My Bookings 🐾"
        subtitle="Track booking status, consultation access, prescriptions, payments, refunds, and reviews in one place."
        actions={(
          <>
            <button className="petaar-button--secondary" onClick={exportCsv} disabled={!sortedBookings.length}>Export CSV</button>
            <button className="petaar-button" onClick={fetchBookings} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </>
        )}
      />
      {loadError && (
        <div className="petaar-notice error" style={{ marginBottom: 16 }}>
          {loadError}
        </div>
      )}
      <div className="petaar-stat-grid" style={{ marginBottom: 20 }}>
        <StatCard icon="📚" label="Total" value={bookingSummary.total} />
        <StatCard icon="⏳" label="Active" value={bookingSummary.active} tone="warning" />
        <StatCard icon="✅" label="Completed" value={bookingSummary.completed} tone="success" />
        <StatCard icon="↩️" label="Refunded" value={bookingSummary.refunded} tone="danger" />
      </div>
      <div className="petaar-card" style={{ marginBottom: 16 }}>
        <div className="petaar-form-grid">
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by service, provider, date, or status"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="pending_assignment">pending_assignment</option>
          <option value="pending">pending</option>
          <option value="accepted">accepted</option>
          <option value="in_progress">in_progress</option>
          <option value="completed">completed</option>
          <option value="cancel_requested">cancel_requested</option>
          <option value="cancelled">cancelled</option>
        </select>
        <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
          <option value="">All Payments</option>
          <option value="paid">paid</option>
          <option value="pending">pending</option>
          <option value="failed">failed</option>
        </select>
        <select value={refundFilter} onChange={(e) => setRefundFilter(e.target.value)}>
          <option value="">All Refunds</option>
          <option value="not_requested">not_requested</option>
          <option value="refund_requested">refund_requested</option>
          <option value="refund_processed">refund_processed</option>
        </select>
        <button
          onClick={() => {
            setSearchTerm("");
            setStatusFilter("");
            setPaymentFilter("");
            setRefundFilter("");
            setCurrentPage(1);
          }}
        >
          Clear
        </button>
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value) || 8)}>
          <option value={8}>8 / page</option>
          <option value={12}>12 / page</option>
          <option value={20}>20 / page</option>
        </select>
        </div>
      </div>
      <p style={{ marginTop: 0, color: "var(--muted)" }}>
        Showing {(sortedBookings.length ? ((currentPage - 1) * pageSize) + 1 : 0)}-
        {Math.min(currentPage * pageSize, sortedBookings.length)} of {sortedBookings.length} booking(s).
      </p>

      {loading ? (
        <div className="petaar-card">Loading bookings...</div>
      ) : sortedBookings.length === 0 ? (
        <EmptyState
          icon="📅"
          title="No bookings yet!"
          description="Browse services and your confirmed bookings, consultations, and refunds will show up here."
        />
      ) : (
        pagedBookings.map((b) => (
          <div
            key={b.id}
            className="petaar-list-row"
            style={{ marginTop: 10 }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div>
                <h3>{b.serviceTitle || b.serviceName || "Service"}</h3>
                <p style={{ color: "var(--muted)" }}>Verified Provider ✓</p>
              </div>
              <ToneBadge tone={getStatusTone(b)}>{getBookingStatusLabel(b)}</ToneBadge>
            </div>
            <p>Amount: ₹{(Number(b.amount) || 0).toFixed(2)}</p>
            <p>
              Platform Fee (20%): ₹{(Number(b.commissionAmount) || (Number(b.amount) || 0) * 0.2).toFixed(2)} | Provider Payout (80%): ₹{(Number(b.providerEarning) || (Number(b.amount) || 0) * 0.8).toFixed(2)}
            </p>
            <p>Payment: {b.paymentStatus || "unknown"}</p>
            <p>
              Booking Date: {b.bookingDate || "N/A"}
              {b.bookingSlot ? ` | Slot: ${b.bookingSlot}` : ""}
            </p>
            {b.rescheduleReason && (
              <p>
                Rescheduled: {b.bookingDate || "N/A"} ({b.rescheduleReason})
              </p>
            )}
            {isVetConsultationBooking(b) && (
              <>
                <div style={{ border: "1px solid #dbe3ee", borderRadius: 8, padding: 8, marginBottom: 8, background: "#f8fafc" }}>
                  <strong>Consultation Details</strong>
                  <p style={{ margin: "6px 0" }}>Mode: {b.consultationMode || "chat"} | Status: {getConsultationStatusLabel(b)}</p>
                  {canJoinConsultationCall(b) ? (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                      <button type="button" onClick={() => openConsultationCallWindow(b)}>
                        Join Secure {getConsultationJoinLabel(b.consultationMode)}
                      </button>
                      <small style={{ color: "#64748b" }}>Opens private Petaar consultation room.</small>
                    </div>
                  ) : (
                    <small style={{ color: "#64748b", display: "block", marginBottom: 8 }}>
                      Use Petaar chat below for this consultation mode.
                    </small>
                  )}
                  {b.assignedVetProfile ? (
                    <>
                      <p style={{ margin: "6px 0" }}>
                        Vet: {b.assignedVetProfile.displayName || "Verified Vet"} ({b.assignedVetProfile.yearsOfService || 0}+ years)
                      </p>
                      <p style={{ margin: "6px 0" }}>
                        Workplace: {b.assignedVetProfile.currentWorkplace || "N/A"} | Location: {b.assignedVetProfile.location || "N/A"}
                      </p>
                      <p style={{ margin: "6px 0" }}>
                        Languages: {(b.assignedVetProfile.languages || []).join(", ") || "N/A"}
                      </p>
                    </>
                  ) : (
                    <p style={{ margin: "6px 0" }}>Vet assignment in progress...</p>
                  )}
                  <p style={{ margin: "6px 0", color: "#64748b" }}>
                    Calls/chats are routed via Petaar. Vet personal number is hidden.
                  </p>
                  {!["completed", "cancelled"].includes(String(b.status || "").toLowerCase()) && (
                    <button
                      type="button"
                      onClick={() => openConsultationChat(b.id)}
                      style={{
                        border: "1px solid #1d4ed8",
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        borderRadius: 8,
                        padding: "6px 10px",
                        fontWeight: 700,
                      }}
                    >
                      Open Chat
                    </button>
                  )}
                </div>
              </>
            )}
            {prescriptionsByBooking[b.id] && (
              <div style={{ border: "1px solid #bbf7d0", borderRadius: 8, padding: 10, marginBottom: 8, background: "#f0fdf4" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleLogoHomeClick}
                    aria-label="Go to home page"
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      display: "inline-flex",
                      cursor: "pointer",
                    }}
                  >
                    <img src="/logo192.png" alt="Petaar" style={{ width: 26, height: 26, borderRadius: 6 }} />
                  </button>
                  <strong>Petaar Prescription</strong>
                </div>
                {prescriptionsByBooking[b.id].diagnosis && (
                  <p style={{ margin: "6px 0" }}>
                    Diagnosis: {prescriptionsByBooking[b.id].diagnosis}
                  </p>
                )}
                <p style={{ margin: "6px 0" }}>
                  Medicines: {prescriptionsByBooking[b.id].medicines || "N/A"}
                </p>
                {prescriptionsByBooking[b.id].advice && (
                  <p style={{ margin: "6px 0" }}>
                    Advice: {prescriptionsByBooking[b.id].advice}
                  </p>
                )}
                {prescriptionsByBooking[b.id].followUpDate && (
                  <p style={{ margin: "6px 0" }}>
                    Follow-up Date: {prescriptionsByBooking[b.id].followUpDate}
                  </p>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => openBrandedPrescription({
                      prescription: prescriptionsByBooking[b.id],
                      booking: b,
                    })}
                  >
                    Open / Print Prescription
                  </button>
                </div>
              </div>
            )}
            {(b.refundStatus === "refund_requested" || b.refundStatus === "refund_processed") && (
              <div style={{ border: "1px solid #dbe3ee", borderRadius: 8, padding: 8, marginBottom: 8 }}>
                <strong>Refund Timeline</strong>
                <p style={{ margin: "6px 0" }}>
                  Requested: {b.refundRequestedAt?.seconds ? new Date(b.refundRequestedAt.seconds * 1000).toLocaleString() : "Pending timestamp"}
                </p>
                <p style={{ margin: "6px 0" }}>
                  Processed: {b.refundProcessedAt?.seconds ? new Date(b.refundProcessedAt.seconds * 1000).toLocaleString() : "Not processed yet"}
                </p>
              </div>
            )}
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 8, marginBottom: 8 }}>
              <strong>Booking Timeline</strong>
              <p style={{ margin: "6px 0" }}>Created: {formatTs(b.createdAt)}</p>
              <p style={{ margin: "6px 0" }}>Paid: {formatTs(b.paidAt)}</p>
              <p style={{ margin: "6px 0" }}>Assigned: {formatTs(b.assignedAt)}</p>
              <p style={{ margin: "6px 0" }}>Accepted: {formatTs(b.acceptedAt)}</p>
              <p style={{ margin: "6px 0" }}>In Progress: {formatTs(b.inProgressAt)}</p>
              <p style={{ margin: "6px 0" }}>Completed: {formatTs(b.completedAt)}</p>
              <p style={{ margin: "6px 0" }}>Rescheduled: {formatTs(b.rescheduleRequestedAt)}</p>
              <p style={{ margin: "6px 0" }}>Cancel Requested: {formatTs(b.cancelRequestedAt)}</p>
              <p style={{ margin: "6px 0" }}>Cancelled: {formatTs(b.cancelledAt)}</p>
            </div>
            {getSlaText(b) && (
              <p style={{ color: "#1d4ed8", marginTop: 0 }}>{getSlaText(b)}</p>
            )}
            {(b.status === "pending_assignment" || b.status === "pending" || b.status === "accepted") && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {isReschedulable(b) && (
                  <>
                    <input
                      type="date"
                      min={new Date().toISOString().slice(0, 10)}
                      value={rescheduleDateByBooking[b.id] || ""}
                      onChange={(e) => setRescheduleDateByBooking((prev) => ({
                        ...prev,
                        [b.id]: e.target.value,
                      }))}
                    />
                    <select
                      value={rescheduleReasonByBooking[b.id] || ""}
                      onChange={(e) => setRescheduleReasonByBooking((prev) => ({
                        ...prev,
                        [b.id]: e.target.value,
                      }))}
                    >
                      <option value="">Reschedule reason</option>
                      <option value="Schedule conflict">Schedule conflict</option>
                      <option value="Pet not available">Pet not available</option>
                      <option value="Need different date">Need different date</option>
                      <option value="Emergency">Emergency</option>
                    </select>
                    <button
                      onClick={() => requestReschedule(b)}
                      disabled={updatingBookingId === b.id}
                    >
                      {updatingBookingId === b.id ? "Updating..." : "Reschedule Date"}
                    </button>
                  </>
                )}
                <select
                  value={cancelReasonByBooking[b.id] || ""}
                  onChange={(e) => setCancelReasonByBooking((prev) => ({
                    ...prev,
                    [b.id]: e.target.value,
                  }))}
                >
                  <option value="">Select cancel reason</option>
                  <option value="Changed plans">Changed plans</option>
                  <option value="Booked by mistake">Booked by mistake</option>
                  <option value="Found another provider">Found another provider</option>
                  <option value="Schedule conflict">Schedule conflict</option>
                  <option value="Emergency">Emergency</option>
                </select>
                <button
                  onClick={() => requestCancellation(b)}
                  disabled={updatingBookingId === b.id}
                >
                  {updatingBookingId === b.id ? "Requesting..." : "Request Cancellation"}
                </button>
                {canRetryAssignment(b) && (
                  <button
                    onClick={() => retryProviderAssignment(b)}
                    disabled={retryingAssignmentId === b.id || updatingBookingId === b.id}
                  >
                    {retryingAssignmentId === b.id ? "Retrying..." : "Find Available Provider"}
                  </button>
                )}
              </div>
            )}

            {reviewsByBooking[b.id] ? (
              <div style={{ marginTop: 10, padding: 10, background: "#f8fafc", borderRadius: 6 }}>
                <strong>Your Review</strong>
                <p style={{ margin: "6px 0" }}>Rating: {reviewsByBooking[b.id].rating}/5</p>
                <p style={{ margin: 0 }}>{reviewsByBooking[b.id].comment}</p>
                {reviewsByBooking[b.id].providerReply && (
                  <div style={{ marginTop: 8, border: "1px solid #dbe3ee", borderRadius: 8, padding: 8, background: "#fff" }}>
                    <strong>Provider Reply</strong>
                    <p style={{ margin: "6px 0 0" }}>{reviewsByBooking[b.id].providerReply}</p>
                  </div>
                )}
              </div>
            ) : (
              b.paymentStatus === "paid" &&
              (b.status === "accepted" || b.status === "completed") && (
                <div style={{ marginTop: 10 }}>
                  <strong>Add Review</strong>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      placeholder="Rating (1-5)"
                      value={drafts[b.id]?.rating || ""}
                      onChange={(e) => setDrafts((prev) => ({
                        ...prev,
                        [b.id]: {
                          ...(prev[b.id] || {}),
                          rating: e.target.value,
                        },
                      }))}
                    />
                    <input
                      placeholder="Write your review"
                      style={{ width: "100%", maxWidth: 380, boxSizing: "border-box" }}
                      value={drafts[b.id]?.comment || ""}
                      onChange={(e) => setDrafts((prev) => ({
                        ...prev,
                        [b.id]: {
                          ...(prev[b.id] || {}),
                          comment: e.target.value,
                        },
                      }))}
                    />
                    <button
                      onClick={() => saveReview(b)}
                      disabled={savingId === b.id}
                    >
                      {savingId === b.id ? "Saving..." : "Submit Review"}
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        ))
      )}
      {sortedBookings.length > 0 && totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
          <button onClick={() => setCurrentPage(1)} disabled={currentPage <= 1}>First</button>
          <button onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage <= 1}>
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>Last</button>
        </div>
      )}
    </div>
  );
}
